/**
 * Lógica pura de conteo cíclico / inventario físico (CC-6: NUNCA usar macros
 * lingui aquí).
 *
 * Un documento de conteo (inventory_count) agrupa líneas (inventory_count_line)
 * por ubicación. Cada línea captura el on-hand TEÓRICO (theoretical_qty) al
 * añadirla y la cantidad CONTADA por el usuario (counted_qty). Al validar el
 * conteo (borrador→validado) se genera, por cada línea con diferencia, un
 * stock_move de AJUSTE cuyo delta (contado − teórico) cuadra el on-hand.
 *
 * Cantidades en ENTEROS (unidades menores, factor 1000), igual que F1–F3.
 */

export type CountState = "draft" | "done";

export const COUNT_STATES: readonly CountState[] = ["draft", "done"] as const;

export function isCountState(value: string): value is CountState {
  return (COUNT_STATES as readonly string[]).includes(value);
}

export function coerceCountState(value: string | null | undefined): CountState {
  return value && isCountState(value) ? value : "draft";
}

/** ¿Se puede validar el conteo? Sólo desde borrador. */
export function canValidateCount(state: string | null | undefined): boolean {
  return coerceCountState(state) === "draft";
}

/** Línea de conteo (subset relevante para el cálculo del ajuste). */
export interface CountLineLike {
  product_id: string | null;
  lot_id: string | null;
  /** On-hand teórico capturado al añadir la línea (unidades menores). */
  theoretical_qty: number | null;
  /** Cantidad contada por el usuario (unidades menores). */
  counted_qty: number | null;
}

/**
 * Delta de ajuste de una línea: contado − teórico. >0 sobra stock (entrada),
 * <0 falta stock (salida), 0 sin diferencia.
 */
export function countLineDelta(line: CountLineLike): number {
  return (line.counted_qty ?? 0) - (line.theoretical_qty ?? 0);
}

/** Ajuste a materializar para una línea de conteo con diferencia. */
export interface CountAdjustment {
  productId: string;
  lotId: string | null;
  /** Delta a aplicar al on-hand (unidades menores, != 0). */
  delta: number;
  /** On-hand final tras el ajuste = cantidad contada. */
  newOnHand: number;
}

/**
 * Planifica los ajustes de un conteo: un CountAdjustment por cada línea cuyo
 * delta sea != 0. Las líneas sin diferencia (o sin producto) se ignoran (no
 * generan move). Determinista, sin efectos.
 */
export function planCountAdjustments(lines: CountLineLike[]): CountAdjustment[] {
  const adjustments: CountAdjustment[] = [];
  for (const line of lines) {
    const productId = line.product_id;
    if (!productId) {
      continue;
    }
    const delta = countLineDelta(line);
    if (delta === 0) {
      continue;
    }
    adjustments.push({
      productId,
      lotId: line.lot_id ?? null,
      delta,
      newOnHand: line.counted_qty ?? 0,
    });
  }
  return adjustments;
}

/** Total de unidades que cambiarán de signo positivo (sobrantes). */
export function countSurplus(lines: CountLineLike[]): number {
  return lines.reduce((acc, l) => acc + Math.max(countLineDelta(l), 0), 0);
}

/** Total de unidades faltantes (mermas) detectadas por el conteo. */
export function countShortage(lines: CountLineLike[]): number {
  return lines.reduce((acc, l) => acc + Math.max(-countLineDelta(l), 0), 0);
}

// ─── ALERTA DE CADUCIDAD PRÓXIMA ────────────────────────────────────────────

/** Lote con su caducidad (subset para la alerta). */
export interface ExpiringLotLike {
  id: string;
  name: string | null;
  product_id: string | null;
  expiry_date: string | null;
}

/** Días enteros entre dos fechas ISO (YYYY-MM-DD). b − a, redondeado hacia abajo. */
export function daysBetween(a: string, b: string): number {
  const MS_PER_DAY = 86_400_000;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) {
    return Number.NaN;
  }
  return Math.floor((tb - ta) / MS_PER_DAY);
}

/** Lote próximo a caducar (o ya caducado) con sus días restantes. */
export interface ExpiryAlert {
  lot: ExpiringLotLike;
  /** Días hasta la caducidad (negativo = ya caducado). */
  daysToExpiry: number;
  /** ¿Ya está caducado a `today`? */
  expired: boolean;
}

/**
 * Calcula las alertas de caducidad próxima: lotes cuya fecha de caducidad cae
 * dentro de los próximos `withinDays` días (o ya caducados). Ignora lotes sin
 * fecha. Ordena por caducidad más cercana primero (los caducados encabezan).
 * Determinista: `today` se pasa como ISO (YYYY-MM-DD) para no depender del reloj.
 */
export function computeExpiryAlerts(
  lots: ExpiringLotLike[],
  today: string,
  withinDays: number,
): ExpiryAlert[] {
  const alerts: ExpiryAlert[] = [];
  for (const lot of lots) {
    if (!lot.expiry_date) {
      continue;
    }
    const days = daysBetween(today, lot.expiry_date);
    if (Number.isNaN(days)) {
      continue;
    }
    if (days <= withinDays) {
      alerts.push({ lot, daysToExpiry: days, expired: days < 0 });
    }
  }
  return alerts.toSorted((a, b) => a.daysToExpiry - b.daysToExpiry);
}
